export const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';
export const ANTHROPIC_VERSION = '2023-06-01';

// Default URL for the local Claude Code shim (see tools/claude-code-shim).
// Used during dev to avoid burning API credits — the shim drives the Claude
// Agent SDK, which authenticates against the user's Claude Code session
// (subscription credit) instead of an API key. Caching + forced tool_choice
// are emulated; see the shim README for the exact compatibility shape.
export const LOCAL_SHIM_URL = 'http://localhost:8642/v1/messages';

export const MODELS = {
  haiku: 'claude-haiku-4-5-20251001',
  sonnet: 'claude-sonnet-4-6',
  opus: 'claude-opus-4-7',
} as const;

export type ModelId = (typeof MODELS)[keyof typeof MODELS];

export interface TaskModels {
  ingest: ModelId;
  qa: ModelId;
  validate: ModelId;
}

export const TASK_MODEL_DEFAULTS: TaskModels = {
  ingest: MODELS.sonnet,
  qa: MODELS.haiku,
  validate: MODELS.haiku,
};

export type LlmBackend = 'anthropic' | 'local-shim';

export interface Settings {
  taskModels: TaskModels;
  pomodoroWorkMin: number;
  pomodoroBreakMin: number;
  pomodoroLongBreakMin: number;
  dailyNewCardCap: number;
  dailyReviewCardCap: number;
  dailySessionGoalMin: number;
  userInterests: string;
  // Extended-thinking budget in tokens for chat + Q&A (0 disables). Kept
  // small by default ("think a little"); ignored on the card/summary
  // generation paths, which force tool_choice and so can't use thinking.
  thinkingBudget: number;
  // How many prior user/assistant turns to replay into chat + Q&A. Most
  // questions are one-off, so a small window keeps cost down; the windowed
  // prefix is cached. <0 keeps the whole thread; 0 sends none. Must stay in
  // sync with the server (resolvedSettings.ChatHistoryTurns).
  chatHistoryTurns: number;
  // 'anthropic' uses the official API + apiKey. 'local-shim' targets
  // shimUrl (defaulting to LOCAL_SHIM_URL) and skips the api-key header,
  // letting Tish iterate without burning subscription/API credits.
  llmBackend: LlmBackend;
  shimUrl: string;
}

// UI presets for the extended-thinking budget (tokens). 0 disables thinking;
// the rest trade latency/cost for more reasoning on chat + Q&A.
export const THINKING_OPTIONS: { label: string; value: number }[] = [
  { label: 'Off', value: 0 },
  { label: 'A little', value: 1024 },
  { label: 'More', value: 4096 },
  { label: 'A lot', value: 8192 },
];

export const DEFAULT_SETTINGS: Settings = {
  taskModels: TASK_MODEL_DEFAULTS,
  pomodoroWorkMin: 25,
  pomodoroBreakMin: 5,
  pomodoroLongBreakMin: 15,
  dailyNewCardCap: 20,
  dailyReviewCardCap: 30,
  dailySessionGoalMin: 90,
  userInterests: '',
  thinkingBudget: 1024,
  chatHistoryTurns: 4,
  llmBackend: 'anthropic',
  shimUrl: LOCAL_SHIM_URL,
};

/**
 * Trim a replayed chat/Q&A history to the last `turns` user/assistant pairs.
 * Mirrors the server's anthropic.WindowHistory: turns < 0 keeps everything,
 * 0 drops history entirely, > 0 keeps the last turns*2 messages.
 * Empty-content messages are dropped first: the API rejects empty text blocks
 * (and rejects cache_control on them outright), so one empty persisted row
 * would otherwise 400 every later turn in the thread.
 */
export function windowHistory<T extends { content: string }>(msgs: T[], turns: number): T[] {
  const nonEmpty = msgs.filter((m) => m.content !== '');
  if (turns < 0) return nonEmpty;
  if (turns === 0) return [];
  return nonEmpty.slice(-turns * 2);
}

// Approximate per-1M-token rates in USD. Keep deliberately rough — only used
// to surface a running monthly total. Refresh when prices change.
export const COST_RATES_PER_M: Record<
  ModelId,
  { input: number; output: number; cacheRead: number; cacheWrite: number }
> = {
  [MODELS.haiku]: { input: 1, output: 5, cacheRead: 0.1, cacheWrite: 1.25 },
  [MODELS.sonnet]: { input: 3, output: 15, cacheRead: 0.3, cacheWrite: 3.75 },
  [MODELS.opus]: { input: 15, output: 75, cacheRead: 1.5, cacheWrite: 18.75 },
};

// Chrome storage key for the cross-content chat pin state.
export const CHAT_PIN_STATE_KEY = 'chatPinState';

// Chrome storage key for the active test — the test new captures attach to
// and the one the UI is currently focused on. Client-local preference only;
// never synced to the server.
export const ACTIVE_TEST_KEY = 'activeTestId';

// Chrome storage key for the currently-selected chat thread. Client-local
// preference so reopening the side panel returns to the same conversation.
export const ACTIVE_CHAT_KEY = 'activeChatId';

// Broadcast message type emitted by the SW after a successful ingest.
// Listeners refresh chapter-derived UI state.
export const MSG_INGEST_DONE = 'INGEST_DONE';

// ---- Scenario roleplay ----

// Precomputed starter inputs for the Scenario tab. `prompt` is the free-form
// text fed to generation when the chip is chosen — keep them domain-general so
// they read sensibly across whichever test is active.
export interface ScenarioPreset {
  id: string;
  label: string;
  prompt: string;
}

export const SCENARIO_PRESETS: ScenarioPreset[] = [
  {
    id: 'client-meeting',
    label: 'Client meeting',
    prompt:
      'A prospective client comes in with competing goals and a fixed time horizon. Build a realistic suitability/recommendation scenario.',
  },
  {
    id: 'suitability',
    label: 'Suitability call',
    prompt:
      'Present a customer profile (age, risk tolerance, objectives, constraints) and ask which recommendation best fits the suitability requirements.',
  },
  {
    id: 'rules-violation',
    label: 'Rules & ethics',
    prompt:
      'Describe a situation that may involve a regulatory or ethics question, and ask which course of action is most compliant.',
  },
  {
    id: 'exam-curveball',
    label: 'Exam curveball',
    prompt:
      'Generate a tricky, exam-style applied scenario that tests a commonly-confused concept from the active material.',
  },
  {
    id: 'series-7-exam',
    label: 'Series 7 exam item',
    prompt:
      'Write a scenario exactly as it would appear on the FINRA Series 7 exam: a concise case study giving a customer profile (age, income, net worth, risk tolerance, time horizon, liquidity needs, tax status) and ask "Which of the following is the MOST suitable recommendation?" Favor the heavily-weighted Function 3 material (recommendations, suitability, product features). Make the four options all plausible so two look reasonable and the learner must pick the single most suitable one. Do not address the learner directly or invent extra coaching framing — keep it terse and exam-formal.',
  },
];

// gradeScenario decides whether the picked option was the best strategy. Keys
// off the option's quality rank (1 = best) rather than a stored index so it
// stays correct even if options get reordered. Returns the chosen option's
// rank (null when the index is out of range) for "you ranked Nth" feedback.
export function gradeScenario(
  options: { rank: number }[],
  selectedOption: number
): { correct: boolean; rank: number | null } {
  const opt = options[selectedOption];
  if (!opt) return { correct: false, rank: null };
  return { correct: opt.rank === 1, rank: opt.rank };
}
